import { useMemo, useState } from 'react';
import { useOpenOperationRow } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { ConceptLabEntry } from '../../types';
import { DataTable, type ColumnDef } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';
import { buildQuestionDisplayText } from '@padcanvas/shared';

const pageSize = 20;

const formatLatency = (ms?: number) => (ms ? `${(ms / 1000).toFixed(1)}s` : '-');

export function ConceptLabList() {
  const openRow = useOpenOperationRow();
  const [page, setPage] = useState(1);
  const [provider, setProvider] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['conceptLabRequests', page, provider],
    queryFn: () => adminApi.getConceptLabRequests({
      page,
      pageSize,
      provider: provider || undefined,
    }),
  });

  const rows = data?.conceptLabEntries || [];
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const columns: ColumnDef<ConceptLabEntry>[] = useMemo(() => [
    {
      key: 'date',
      header: 'Date',
      className: 'dt-col-date',
      render: (e) => (
        <div className="dc-stack">
          <span className="dc-date">{format(new Date(e.conceptLab.generatedAt), 'MMM d, HH:mm')}</span>
          {e.conceptLab.status === 'generating' && (
            <span className="dc-status dc-status--analyzing" title="Concept lab generation in flight">GENERATING</span>
          )}
          {e.conceptLab.status === 'failed' && (
            <span className="dc-status dc-status--failed" title="Concept lab generation failed">FAILED</span>
          )}
        </div>
      ),
    },
    {
      key: 'user',
      header: 'User',
      render: (e) => (
        <div className="dc-stack">
          <span className="dc-name">{e.user?.name || 'Unknown'}</span>
          <span className="dc-email">{e.user?.email || '-'}</span>
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      className: 'dt-col-subject',
      render: (e) => {
        const subj = e.question?.subject || '';
        return (
          <div className="dc-stack">
            <span className={`dc-subject ${subj ? `dc-subject--${subj}` : ''}`}>{subj || '-'}</span>
            <span className="dc-topic">{e.question?.topic || '-'}</span>
          </div>
        );
      },
    },
    {
      key: 'question',
      header: 'Question',
      render: (e) => e.question?.questionText
        ? <LatexText text={buildQuestionDisplayText(e.question)} truncate={100} />
        : <span className="dc-no-value">-</span>,
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (e) => <span className="provider-badge">{e.conceptLab.provider || '-'}</span>,
    },
    {
      key: 'interactions',
      header: 'Interactions',
      render: (e) => <span>{e.conceptLab.interactions?.length || 0}</span>,
    },
    {
      key: 'latency',
      header: 'Latency',
      render: (e) => e.conceptLab.latencyMs != null ? formatLatency(e.conceptLab.latencyMs) : '-',
    },
  ], []);

  const toolbar = (
    <select
      className="filter-select"
      value={provider}
      onChange={(e) => { setProvider(e.target.value); setPage(1); }}
    >
      <option value="">All Providers</option>
      <option value="claude">Claude</option>
      <option value="openai">OpenAI</option>
      <option value="gemini">Gemini</option>
    </select>
  );

  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <DataTable
      columns={columns}
      data={rows}
      rowKey={(e) => e.id}
      onRowClick={(e) => openRow(rows, e, '/conceptlab', { page })}
      pagination={{ page, totalPages, total: data?.total, onPageChange: setPage }}
      toolbar={toolbar}
      loading={isLoading}
      emptyMessage="No concept lab entries yet."
    />
  );
}
